import React from 'react';
import styled from 'styled-components';
import { Folder, Edit2, Trash2 } from 'lucide-react';

interface FolderCardProps {
  id: number;
  name: string;
  icon: string;
  color: string;
  totalSets: number;
  description?: string;
  createdBy?: number;
  creatorName?: string;
  currentUserId?: number;
  currentUserRole?: number;
  onSelect: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

const Card = styled.div<{ color: string }>`
  background: white;
  border: 2px solid #e5e7eb;
  border-left: 4px solid ${props => props.color};
  border-radius: 12px;
  padding: 16px 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  position: relative;

  &:hover {
    transform: translateX(4px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    border-color: ${props => props.color};
    background: ${props => props.color}05;
  }
`;

const CardContent = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
`;

const IconWrapper = styled.div<{ color: string }>`
  font-size: 32px;
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.color}15;
  border-radius: 10px;
  flex-shrink: 0;
`;

const FolderInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const CardTitle = styled.h3`
  margin: 0 0 4px 0;
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CardDescription = styled.p`
  margin: 0;
  font-size: 13px;
  color: #9ca3af;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CreatorInfo = styled.p`
  margin: 0;
  font-size: 12px;
  color: #9ca3af;
  margin-top: 2px;
`;

const SetCount = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: #f3f4f6;
  border-radius: 6px;
  font-size: 13px;
  color: #6b7280;
  font-weight: 600;
  white-space: nowrap;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  margin-left: auto;
`;

const ActionButton = styled.button`
  background: white;
  border: 1px solid #e5e7eb;
  padding: 8px 12px;
  cursor: pointer;
  color: #6b7280;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 6px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;

  &:hover {
    background: #f9fafb;
    color: #1f2937;
    border-color: #9ca3af;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

export const FolderCard: React.FC<FolderCardProps> = ({
  id,
  name,
  icon,
  color,
  totalSets,
  description,
  createdBy,
  creatorName,
  currentUserId,
  currentUserRole,
  onSelect,
  onEdit,
  onDelete,
}) => {
  // Check if current user is the creator or admin
  const isOwner = currentUserId && createdBy && currentUserId === createdBy;
  const isAdmin = currentUserRole === 0;
  const canManage = isOwner || isAdmin;
  // Show creator info only for non-students (role !== 2)
  const shouldShowCreatorInfo = currentUserRole !== 2 && creatorName;

  return (
    <Card color={color} onClick={onSelect}>
      <CardContent>
        <IconWrapper color={color}>{icon}</IconWrapper>
        <CardHeader>
          <FolderInfo>
            <CardTitle>{name}</CardTitle>
            {description && <CardDescription>{description}</CardDescription>}
            {shouldShowCreatorInfo && (
              <CreatorInfo>👤 {creatorName} (user id: {createdBy})</CreatorInfo>
            )}
          </FolderInfo>
        </CardHeader>
      </CardContent>

      <SetCount>📚 {totalSets} Bộ từ</SetCount>

      <ActionButtons>
        {canManage && (
          <>
            <ActionButton
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
              title="Chỉnh sửa folder"
            >
              <Edit2 /> Sửa
            </ActionButton>
            <ActionButton
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              title="Xóa folder"
            >
              <Trash2 /> Xóa
            </ActionButton>
          </>
        )}
      </ActionButtons>
    </Card>
  );
};
